import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, Loader2, MessageCircle, Smartphone, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  DEFAULT_WHATSAPP_TEMPLATES,
  WHATSAPP_REMINDER_HOURS,
  WHATSAPP_TEMPLATE_KINDS,
  WHATSAPP_TEMPLATE_LABEL,
  WHATSAPP_TEMPLATE_MAX,
  WHATSAPP_VARIABLES,
  renderWhatsappTemplate,
  whatsappSettingsInput,
  whatsappTestInput,
  type WhatsappConnectResult,
  type WhatsappStatusResult,
  type WhatsappTemplateKind,
  type WhatsappView,
} from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { MaskedInput } from '@/components/shared/masked-input';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Pill } from '@/features/finance/pill';
import { api, errorMessage } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { startConnectPolling } from './whatsapp-polling';

export const WA_QUERY_KEY = ['whatsapp'] as const;

const SAMPLE = {
  cliente: 'João',
  servico: 'Corte e barba',
  profissional: 'Pedro',
  data: '15/11/2026',
  hora: '14:30',
  barbearia: 'Sua barbearia',
  link: 'https://seusite.com.br/b/sua-barbearia/agendamento/abc123',
};

const STATUS_PILL = {
  disconnected: { label: 'Desconectado', tone: 'neutral' },
  connecting: { label: 'Conectando', tone: 'warn' },
  connected: { label: 'Conectado', tone: 'good' },
} as const;

export const WA_WARNING = 'Use um número da própria barbearia; envios em massa podem causar bloqueio do número. O app só envia mensagens de agendamento.';

/** Cartão do WhatsApp em Configurações > Integrações. */
export function WhatsappCard() {
  const q = useQuery({ queryKey: WA_QUERY_KEY, queryFn: () => api.get<WhatsappView>('/whatsapp') });
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  return <WhatsappPanel view={q.data} />;
}

/** Visual do cartão a partir do estado já carregado (separado para teste). */
export function WhatsappPanel({ view }: { view: WhatsappView }) {
  const qc = useQueryClient();
  const tz = useTimezone();
  const [connectOpen, setConnectOpen] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: WA_QUERY_KEY });

  const patch = useMutation({
    mutationFn: (body: z.input<typeof whatsappSettingsInput>) => api.patch<WhatsappView>('/whatsapp/settings', whatsappSettingsInput.parse(body)),
    onSuccess: (v) => qc.setQueryData(WA_QUERY_KEY, v),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const disconnect = useMutation({
    mutationFn: () => api.post<WhatsappView>('/whatsapp/disconnect', {}),
    onSuccess: (v) => {
      qc.setQueryData(WA_QUERY_KEY, v);
      toast.success('WhatsApp desconectado');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const pill = STATUS_PILL[view.status];
  return (
    <section aria-labelledby="wa-title" className="min-w-0 rounded-lg border bg-card">
      <header className="flex flex-wrap items-start gap-3 p-4">
        <MessageCircle className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 id="wa-title" className="flex flex-wrap items-center gap-2 font-medium">
            WhatsApp
            {view.available ? <Pill tone={pill.tone}>{pill.label}</Pill> : <Pill>Indisponível</Pill>}
          </h3>
          <p className="mt-0.5 break-words text-sm text-muted-foreground">
            {!view.available
              ? 'O WhatsApp não está disponível no momento. Fale com o suporte. Nada é enviado.'
              : view.status === 'connected'
                ? `${view.phoneMasked ?? 'Número conectado'}${view.profileName ? ` · ${view.profileName}` : ''}${view.connectedAt ? ` · desde ${fmtDateTime(view.connectedAt, tz)}` : ''}`
                : 'Conecte o WhatsApp da barbearia lendo um QR code. Confirmação, lembrete e cancelamento saem do seu número.'}
          </p>
        </div>
        {view.available && (
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            {view.status === 'connected' ? (
              <ConfirmDialog
                trigger={
                  <Button variant="outline" size="sm" disabled={disconnect.isPending}>
                    Desconectar
                  </Button>
                }
                title="Desconectar o WhatsApp?"
                description="O número será desvinculado e os envios automáticos ficam desligados. Mensagens já agendadas não serão enviadas. Você pode conectar de novo quando quiser."
                confirmLabel="Desconectar"
                onConfirm={() => disconnect.mutateAsync()}
              />
            ) : (
              <Button size="sm" onClick={() => setConnectOpen(true)}>
                <Smartphone aria-hidden /> Conectar
              </Button>
            )}
          </div>
        )}
      </header>

      <div className="flex items-start gap-2 border-t bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-ink" aria-hidden />
        <p>{WA_WARNING}</p>
      </div>

      {view.available && view.status === 'connected' && (
        <div className="grid gap-6 border-t p-4">
          <div className="flex items-start gap-3">
            <Switch
              id="wa-enabled"
              checked={view.enabled}
              disabled={patch.isPending}
              onCheckedChange={(v) => patch.mutate({ enabled: v })}
              aria-describedby="wa-enabled-hint"
              className="mt-0.5"
            />
            <div className="grid gap-0.5">
              <label htmlFor="wa-enabled" className="text-sm font-medium">
                Enviar mensagens automáticas de agendamento
              </label>
              <p id="wa-enabled-hint" className="text-xs text-muted-foreground">
                Clientes marcados como &quot;Não enviar mensagens por WhatsApp&quot; nunca recebem.
              </p>
            </div>
          </div>
          <SettingsForm key={JSON.stringify([view.sendConfirmation, view.sendReminder, view.sendCancellation, view.reminderHours, view.templates])} view={view} onSaved={(v) => qc.setQueryData(WA_QUERY_KEY, v)} />
          <TestSend />
        </div>
      )}

      <ConnectModal open={connectOpen} onOpenChange={setConnectOpen} onConnected={refresh} />
    </section>
  );
}

interface SettingsValues {
  sendConfirmation: boolean;
  sendReminder: boolean;
  sendCancellation: boolean;
  reminderHours: string;
  tplConfirmation: string;
  tplReminder: string;
  tplCancellation: string;
}

const TPL_FIELD = { confirmation: 'tplConfirmation', reminder: 'tplReminder', cancellation: 'tplCancellation' } as const;
const FLAG_FIELD = { confirmation: 'sendConfirmation', reminder: 'sendReminder', cancellation: 'sendCancellation' } as const;

function SettingsForm({ view, onSaved }: { view: WhatsappView; onSaved: (v: WhatsappView) => void }) {
  const form = useForm<SettingsValues>({
    defaultValues: {
      sendConfirmation: view.sendConfirmation,
      sendReminder: view.sendReminder,
      sendCancellation: view.sendCancellation,
      reminderHours: String(view.reminderHours),
      tplConfirmation: view.templates.confirmation ?? view.defaultTemplates.confirmation,
      tplReminder: view.templates.reminder ?? view.defaultTemplates.reminder,
      tplCancellation: view.templates.cancellation ?? view.defaultTemplates.cancellation,
    },
  });
  const { errors } = form.formState;
  const values = form.watch();

  const save = useMutation({
    mutationFn: (v: SettingsValues) => {
      // texto igual ao padrão volta a ser "padrão" (null)
      const tpl = (kind: WhatsappTemplateKind) => {
        const t = v[TPL_FIELD[kind]];
        return t.trim() === DEFAULT_WHATSAPP_TEMPLATES[kind].trim() ? null : t;
      };
      const body = whatsappSettingsInput.parse({
        sendConfirmation: v.sendConfirmation,
        sendReminder: v.sendReminder,
        sendCancellation: v.sendCancellation,
        reminderHours: Number(v.reminderHours),
        tplConfirmation: tpl('confirmation'),
        tplReminder: tpl('reminder'),
        tplCancellation: tpl('cancellation'),
      });
      return api.patch<WhatsappView>('/whatsapp/settings', body);
    },
    onSuccess: (v) => {
      toast.success('Configurações do WhatsApp salvas');
      onSaved(v);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const validate = (v: SettingsValues) => {
    let ok = true;
    for (const kind of WHATSAPP_TEMPLATE_KINDS) {
      const r = whatsappSettingsInput.shape[TPL_FIELD[kind]].safeParse(v[TPL_FIELD[kind]]);
      if (!r.success) {
        form.setError(TPL_FIELD[kind], { message: r.error.issues[0]?.message ?? 'Texto inválido' });
        ok = false;
      }
    }
    return ok;
  };

  return (
    <form onSubmit={form.handleSubmit((v) => validate(v) && save.mutate(v))} className="grid gap-5" noValidate>
      <fieldset className="grid gap-3">
        <legend className="mb-1 text-sm font-medium">Quais mensagens enviar</legend>
        {WHATSAPP_TEMPLATE_KINDS.map((kind) => (
          <Controller
            key={kind}
            control={form.control}
            name={FLAG_FIELD[kind]}
            render={({ field }) => (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} />
                {WHATSAPP_TEMPLATE_LABEL[kind]}
              </label>
            )}
          />
        ))}
        <Field id="wa-hours" label="Antecedência do lembrete" className="max-w-xs">
          <Controller
            control={form.control}
            name="reminderHours"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="wa-hours" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WHATSAPP_REMINDER_HOURS.map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {h} horas antes
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
      </fieldset>

      <div className="grid gap-1.5">
        <p className="text-sm font-medium">Modelos de mensagem</p>
        <p className="text-xs text-muted-foreground">Variáveis disponíveis:</p>
        <ul className="flex flex-wrap gap-1.5" aria-label="Variáveis disponíveis">
          {Object.entries(WHATSAPP_VARIABLES).map(([k, d]) => (
            <li key={k} title={d} className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{`{{${k}}}`}</li>
          ))}
        </ul>
      </div>

      {WHATSAPP_TEMPLATE_KINDS.map((kind) => {
        const name = TPL_FIELD[kind];
        const id = `wa-tpl-${kind}`;
        return (
          <div key={kind} className="grid gap-2">
            <Field id={id} label={WHATSAPP_TEMPLATE_LABEL[kind]} error={errors[name]?.message} hint={`${values[name].length}/${WHATSAPP_TEMPLATE_MAX} caracteres`}>
              <Textarea id={id} rows={4} maxLength={WHATSAPP_TEMPLATE_MAX} aria-invalid={!!errors[name]} {...form.register(name)} />
            </Field>
            <div className="min-w-0 rounded-md border bg-muted/40 p-3">
              <p className="text-xs font-medium text-muted-foreground">Prévia com dados de exemplo</p>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm" data-testid={`wa-preview-${kind}`}>
                {renderWhatsappTemplate(values[name], SAMPLE) || '—'}
              </p>
            </div>
            <div>
              <Button type="button" variant="ghost" size="sm" onClick={() => form.setValue(name, DEFAULT_WHATSAPP_TEMPLATES[kind], { shouldDirty: true })}>
                Restaurar padrão
              </Button>
            </div>
          </div>
        );
      })}

      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Salvando…' : 'Salvar configurações'}
        </Button>
      </div>
    </form>
  );
}

function TestSend() {
  const form = useForm<z.input<typeof whatsappTestInput>>({ resolver: zodResolver(whatsappTestInput), defaultValues: { phone: '' } });
  const send = useMutation({
    mutationFn: (v: { phone: string }) => api.post('/whatsapp/test', whatsappTestInput.parse(v)),
    onSuccess: () => toast.success('Mensagem de teste enviada'),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <form onSubmit={form.handleSubmit((v) => send.mutate(v))} className="grid gap-2 border-t pt-4" noValidate>
      <p className="text-sm font-medium">Enviar mensagem de teste</p>
      <div className="flex flex-wrap items-start gap-2">
        <Field id="wa-test-phone" label="Telefone para o teste" error={form.formState.errors.phone?.message} className="w-full sm:w-64">
          <MaskedInput mask="phone" id="wa-test-phone" placeholder="(11) 99999-0000" aria-invalid={!!form.formState.errors.phone} {...form.register('phone')} />
        </Field>
        <Button type="submit" variant="outline" disabled={send.isPending} className="sm:mt-[1.375rem]">
          {send.isPending ? 'Enviando…' : 'Enviar teste'}
        </Button>
      </div>
    </form>
  );
}

function ConnectModal({ open, onOpenChange, onConnected }: { open: boolean; onOpenChange: (v: boolean) => void; onConnected: () => void }) {
  const [qr, setQr] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [state, setState] = useState<'waiting' | 'connected' | 'timeout'>('waiting');
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQr(null);
    setSyncing(false);
    setState('waiting');
    setError(null);
    let closeTimer: ReturnType<typeof setTimeout> | undefined;
    const stop = startConnectPolling({
      requestQr: () => api.post<WhatsappConnectResult>('/whatsapp/connect', {}),
      checkStatus: () => api.get<WhatsappStatusResult>('/whatsapp/status'),
      onQr: (r) => {
        setError(null);
        // sem QR com a conexão em andamento = o celular já leu o código e está sincronizando: não mostrar QR velho
        setSyncing(r.qr === null && r.state === 'connecting');
        setQr(r.qr);
        setExpiresAt(Date.now() + r.expiresInSec * 1000);
      },
      onConnected: () => {
        setState('connected');
        toast.success('WhatsApp conectado');
        onConnected();
        closeTimer = setTimeout(() => onOpenChange(false), 1500);
      },
      onTimeout: () => setState('timeout'),
      onError: (e) => setError(errorMessage(e)),
    });
    return () => {
      stop();
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const left = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Conectar WhatsApp"
      description="Leia o QR code com o celular da barbearia."
      size="sm"
      footer={
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {state === 'connected' ? 'Fechar' : 'Cancelar'}
        </Button>
      }
    >
      {state === 'connected' ? (
        <div role="status" className="flex flex-col items-center gap-2 py-8 text-center">
          <CircleCheck className="size-10 text-primary" aria-hidden />
          <p className="font-medium">WhatsApp conectado</p>
        </div>
      ) : (
        <div className="grid gap-4">
          <div className="mx-auto flex aspect-square w-full max-w-64 items-center justify-center rounded-lg border bg-white p-2">
            {syncing && !qr ? (
              <span role="status" className="flex flex-col items-center gap-2 px-4 text-center text-sm text-muted-foreground">
                <Loader2 className="size-5 animate-spin" aria-hidden />
                Conectando e sincronizando com o celular. Isso pode levar alguns minutos; não feche o WhatsApp.
              </span>
            ) : qr ? (
              <img src={qr} alt="QR code para conectar o WhatsApp" className="size-full object-contain" />
            ) : (
              <span role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden /> Gerando QR code…
              </span>
            )}
          </div>
          {qr && (
            <p className="text-center text-xs text-muted-foreground" aria-live="off">
              {left > 0 ? `Atualiza em ${left}s` : 'Atualizando…'}
            </p>
          )}
          {error && (
            <p role="alert" className="text-center text-sm text-destructive">
              {error}
            </p>
          )}
          {state === 'timeout' && (
            <p role="alert" className="text-center text-sm text-destructive">
              O tempo para conectar acabou. Feche e tente de novo.
            </p>
          )}
          <ol className="grid list-decimal gap-1 pl-5 text-sm">
            <li>Abra o WhatsApp no celular.</li>
            <li>Toque em Aparelhos conectados.</li>
            <li>Toque em Conectar um aparelho e aponte a câmera para o QR code.</li>
          </ol>
        </div>
      )}
    </FormModal>
  );
}
